/**
 * Atlas Forms — Style Utility Functions
 *
 * Pure, side-effect-free helpers for the Atlas Forms visual style system.
 * No React state, no side effects — safe to call anywhere.
 *
 * Sections:
 *  1. stylePropertiesToCSS      — StyleProperties → React.CSSProperties
 *  2. stylePropertiesToCSSString — StyleProperties → CSS declaration block string
 *  3. mergeStyleProperties      — Shallow merge of two StyleProperties objects
 *  4. resolveStyles             — Ancestor-chain style resolution
 *  5. buildParentMap            — Build childId → parentId map from schema
 *  6. getApplicableSlots        — Returns style slots for a given control type
 *  7. buildGradientString       — GradientConfig → CSS gradient string
 *  8. isEmptyStyleProperties    — True when every field is undefined/null
 */

import type { CSSProperties } from 'react';
import type {
  StyleProperties,
  ControlStyleSet,
  GradientConfig,
  ParentMap,
} from '@atlas-forms/types-js';
import type { FormSchema, FormControl } from '@atlas-forms/types-js';

// ─── Section 1: stylePropertiesToCSS ─────────────────────────────────────────

/** Fields that should be emitted as `${n}px` strings. */
const PX_FIELDS = new Set<keyof StyleProperties>([
  'fontSize',
  'letterSpacing',
  'gap',
  'rowGap',
  'columnGap',
  'borderWidth',
  'borderTopWidth',
  'borderRightWidth',
  'borderBottomWidth',
  'borderLeftWidth',
  'borderRadius',
  'borderTopLeftRadius',
  'borderTopRightRadius',
  'borderBottomRightRadius',
  'borderBottomLeftRadius',
  'outlineWidth',
  'outlineOffset',
  'paddingTop',
  'paddingRight',
  'paddingBottom',
  'paddingLeft',
  'marginTop',
  'marginRight',
  'marginBottom',
  'marginLeft',
]);

/** Map named fontWeight tokens to numeric equivalents. */
const FONT_WEIGHT_MAP: Record<string, number> = {
  normal: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
};

/**
 * Converts a {@link StyleProperties} object to a React `CSSProperties` object
 * suitable for use as an inline style prop.
 *
 * - Numeric px-fields are converted to `'${n}px'` strings.
 * - `lineHeight`, `opacity`, and `zIndex` are kept unitless.
 * - Named `fontWeight` tokens are resolved to their numeric equivalents.
 * - Undefined and null fields are omitted from the result.
 */
export function stylePropertiesToCSS(props: StyleProperties): CSSProperties {
  const css: Record<string, unknown> = {};

  (Object.keys(props) as Array<keyof StyleProperties>).forEach((key) => {
    const value = props[key];
    if (value === undefined || value === null) return;

    if (key === 'fontWeight') {
      if (typeof value === 'string' && key in FONT_WEIGHT_MAP) {
        css[key] = FONT_WEIGHT_MAP[value as string];
      } else {
        // numeric fontWeight or unrecognised string — pass through
        css[key] = value;
      }
      return;
    }

    if (PX_FIELDS.has(key) && typeof value === 'number') {
      css[key] = `${value}px`;
      return;
    }

    // lineHeight, opacity, zIndex — unitless numbers, pass through
    css[key] = value;
  });

  return css as CSSProperties;
}

// ─── Section 2: stylePropertiesToCSSString ────────────────────────────────────

/** Convert a camelCase CSS property name to kebab-case. */
function camelToKebab(camel: string): string {
  return camel.replace(/([A-Z])/g, (ch) => `-${ch.toLowerCase()}`);
}

/** Format a StyleProperties value as a CSS string token. */
function formatCSSValue(key: keyof StyleProperties, value: unknown): string {
  if (key === 'fontWeight' && typeof value === 'string') {
    return String(FONT_WEIGHT_MAP[value] ?? value);
  }
  if (PX_FIELDS.has(key) && typeof value === 'number') {
    return `${value}px`;
  }
  return String(value);
}

/**
 * Converts a {@link StyleProperties} object to a CSS declaration block string.
 *
 * Each property is placed on its own line as `property-name: value;`.
 * Where all four padding (or margin) sides are defined and equal the shorthand
 * form is used instead of four individual declarations.
 *
 * Useful for injecting styles into the custom-code plugin or `<style>` tags.
 */
export function stylePropertiesToCSSString(props: StyleProperties): string {
  const lines: string[] = [];
  const omit = new Set<keyof StyleProperties>();

  // ── Padding shorthand ──────────────────────────────────────────────────────
  const { paddingTop, paddingRight, paddingBottom, paddingLeft } = props;
  if (
    paddingTop !== undefined &&
    paddingRight !== undefined &&
    paddingBottom !== undefined &&
    paddingLeft !== undefined &&
    paddingTop === paddingRight &&
    paddingRight === paddingBottom &&
    paddingBottom === paddingLeft
  ) {
    lines.push(`padding: ${paddingTop}px;`);
    omit.add('paddingTop');
    omit.add('paddingRight');
    omit.add('paddingBottom');
    omit.add('paddingLeft');
  }

  // ── Margin shorthand ───────────────────────────────────────────────────────
  const { marginTop, marginRight, marginBottom, marginLeft } = props;
  if (
    marginTop !== undefined &&
    marginRight !== undefined &&
    marginBottom !== undefined &&
    marginLeft !== undefined &&
    marginTop === marginRight &&
    marginRight === marginBottom &&
    marginBottom === marginLeft
  ) {
    lines.push(`margin: ${marginTop}px;`);
    omit.add('marginTop');
    omit.add('marginRight');
    omit.add('marginBottom');
    omit.add('marginLeft');
  }

  // ── Border shorthand (width + style + color when all sides agree) ──────────
  const {
    borderWidth,
    borderStyle,
    borderColor,
    borderTopWidth,
    borderRightWidth,
    borderBottomWidth,
    borderLeftWidth,
  } = props;

  // If explicit per-side widths are NOT set and shorthand fields are present,
  // emit a border shorthand.
  const hasSideBorderWidths =
    borderTopWidth !== undefined ||
    borderRightWidth !== undefined ||
    borderBottomWidth !== undefined ||
    borderLeftWidth !== undefined;

  if (!hasSideBorderWidths && borderWidth !== undefined && borderStyle !== undefined) {
    const bColor = borderColor ?? '';
    lines.push(`border: ${borderWidth}px ${borderStyle}${bColor ? ` ${bColor}` : ''};`);
    omit.add('borderWidth');
    omit.add('borderStyle');
    omit.add('borderColor');
  }

  // ── Remaining properties ───────────────────────────────────────────────────
  (Object.keys(props) as Array<keyof StyleProperties>).forEach((key) => {
    if (omit.has(key)) return;
    const value = props[key];
    if (value === undefined || value === null) return;
    lines.push(`${camelToKebab(key)}: ${formatCSSValue(key, value)};`);
  });

  return lines.join('\n');
}

// ─── Section 3: mergeStyleProperties ─────────────────────────────────────────

/**
 * Shallow-merges two {@link StyleProperties} objects.
 *
 * Every property defined in `override` replaces the corresponding property in
 * `base`. Properties that are `undefined` in `override` do NOT erase values
 * already present in `base`.
 */
export function mergeStyleProperties(
  base: StyleProperties,
  override: StyleProperties,
): StyleProperties {
  const result: StyleProperties = { ...base };
  (Object.keys(override) as Array<keyof StyleProperties>).forEach((key) => {
    const val = override[key];
    if (val !== undefined && val !== null) {
      // Each key is a valid StyleProperties key; cast is safe.
      (result as Record<string, unknown>)[key] = val;
    }
  });
  return result;
}

// ─── Section 4: resolveStyles ─────────────────────────────────────────────────

/**
 * Resolves the effective {@link StyleProperties} for a given control slot by
 * walking the ancestor chain defined by `parentMap`.
 *
 * Resolution order (lowest → highest priority):
 *   1. `schema.styles?.[slot]`         — form-level defaults
 *   2. Ancestor control styles          — nearest ancestor wins over further ones
 *   3. The control's own styles         — highest priority
 *
 * Cycle protection: if a control ID is encountered more than once while
 * walking up the chain the traversal stops immediately.
 *
 * Returns `{}` when no styles are found anywhere.
 */
export function resolveStyles(
  controlId: string,
  slot: keyof ControlStyleSet,
  schema: FormSchema,
  parentMap: ParentMap,
): StyleProperties {
  if (!schema) return {};

  // ── Build ancestor chain ───────────────────────────────────────────────────
  // Walk from controlId upward; undefined parent means root form.
  const chain: string[] = [];
  const visited = new Set<string>();

  let current: string | undefined = controlId;
  while (current !== undefined) {
    if (visited.has(current)) break; // cycle guard
    visited.add(current);
    chain.push(current);
    current = parentMap.get(current);
  }

  // ── Collect style layers ───────────────────────────────────────────────────
  // Build a lookup map for O(1) access inside the loop.
  const controlById = new Map<string, FormControl>();
  if (Array.isArray(schema.controls)) {
    for (const ctrl of schema.controls) {
      controlById.set(ctrl.id, ctrl);
    }
  }

  // Gather per-control style layers; chain[0] is the control itself.
  // We want innermost (control itself) to win, so we reverse for the merge.
  const layers: StyleProperties[] = [];

  // Schema-level base comes first (lowest priority).
  const schemaSlot = schema.styles?.[slot];
  if (schemaSlot) layers.push(schemaSlot);

  // Ancestors from outermost to innermost (chain is [self, parent, grandparent, …]).
  for (let i = chain.length - 1; i >= 0; i--) {
    const ctrl = controlById.get(chain[i]);
    const slotStyle = ctrl?.styles?.[slot];
    if (slotStyle) layers.push(slotStyle);
  }

  if (layers.length === 0) return {};

  return layers.reduce(mergeStyleProperties);
}

// ─── Section 5: buildParentMap ────────────────────────────────────────────────

/** Control types that can contain child controls. */
const CONTAINER_TYPES = new Set([
  'card-container',
  'enhanced-tabs',
  'accordion',
  'stepper',
  'collapsible-panel',
  'modal',
  'form-container',
  'form-node',
  'grid',
]);

/**
 * Given an arbitrary config value, extract a flat list of child control IDs.
 *
 * Handles:
 * - `string[]`                        — direct ID arrays
 * - `{ id: string }[]`               — object arrays with an `id` property
 * - `{ controls: string[] | { id }[] }[]` — tab/accordion/stepper items
 * - Nested arrays of the above forms
 * - null / undefined — returns `[]`
 */
function extractChildIds(value: unknown): string[] {
  if (value === null || value === undefined) return [];

  // Plain string array
  if (Array.isArray(value)) {
    const ids: string[] = [];
    for (const item of value) {
      if (typeof item === 'string') {
        ids.push(item);
      } else if (item !== null && typeof item === 'object') {
        const obj = item as Record<string, unknown>;
        // Direct { id: string }
        if (typeof obj['id'] === 'string') {
          ids.push(obj['id'] as string);
        }
        // Item with nested controls / tabs / steps
        for (const nestedKey of ['controls', 'tabs', 'steps', 'items', 'children']) {
          if (nestedKey in obj) {
            ids.push(...extractChildIds(obj[nestedKey]));
          }
        }
      }
    }
    return ids;
  }

  return [];
}

/**
 * Builds a `Map<childId, parentId>` by inspecting all container controls in
 * the schema.
 *
 * Controls that are NOT present as values in the map are direct children of
 * the root form (i.e. `parentMap.get(id)` returns `undefined`).
 *
 * All config access is wrapped in try/catch because `config` is typed as
 * `Record<string, unknown>` and may contain arbitrary data.
 *
 * Cycle detection: a child ID that is already registered as an ancestor in the
 * current traversal path is skipped silently.
 */
export function buildParentMap(schema: FormSchema): ParentMap {
  const map: ParentMap = new Map();

  if (!schema || !Array.isArray(schema.controls)) return map;

  for (const control of schema.controls) {
    if (!CONTAINER_TYPES.has(control.type)) continue;
    if (!control.config) continue;

    try {
      const cfg = control.config as Record<string, unknown>;
      let childIds: string[] = [];

      // children: string[] | { id }[]
      if ('children' in cfg) {
        childIds.push(...extractChildIds(cfg['children']));
      }

      // items: { controls: ... }[]
      if ('items' in cfg) {
        childIds.push(...extractChildIds(cfg['items']));
      }

      // tabs: { controls: ... }[]
      if ('tabs' in cfg) {
        childIds.push(...extractChildIds(cfg['tabs']));
      }

      // steps: { controls: ... }[]
      if ('steps' in cfg) {
        childIds.push(...extractChildIds(cfg['steps']));
      }

      // Deduplicate and register, guarding against cycles.
      const uniqueIds = [...new Set(childIds)];
      for (const childId of uniqueIds) {
        if (childId === control.id) continue; // self-reference guard
        if (!map.has(childId)) {
          map.set(childId, control.id);
        }
        // If already registered under a different parent, first-write wins;
        // this mirrors a depth-first traversal where the first container
        // encountered claims ownership.
      }
    } catch {
      // Defensive: skip any control whose config cannot be safely inspected.
      continue;
    }
  }

  return map;
}

// ─── Section 6: getApplicableSlots ───────────────────────────────────────────

/**
 * Returns the list of {@link ControlStyleSet} slot keys that are applicable
 * to the given control type.
 *
 * Unknown / custom control types default to `['containerStyle']`.
 */
export function getApplicableSlots(controlType: string): (keyof ControlStyleSet)[] {
  switch (controlType) {
    // Input controls
    case 'text':
    case 'email':
    case 'number':
    case 'url':
    case 'password':
    case 'tel':
    case 'textarea':
    case 'select':
    case 'multiselect':
    case 'date':
    case 'datetime':
    case 'time-picker':
    case 'slider-range':
    case 'color-picker':
      return ['containerStyle', 'headerStyle', 'bodyStyle'];

    // Toggle controls
    case 'checkbox':
    case 'radio':
    case 'switch':
      return ['containerStyle', 'headerStyle', 'contentStyle'];

    // Complex input controls
    case 'tag-input':
    case 'key-value-pairs':
      return ['containerStyle', 'headerStyle', 'bodyStyle', 'contentStyle'];

    // Action controls
    case 'button':
      return ['containerStyle', 'contentStyle', 'buttonStyle'];

    case 'link':
      return ['containerStyle', 'contentStyle'];

    // Display text controls
    case 'label':
    case 'header':
      return ['containerStyle', 'contentStyle'];

    // Rich content / viewer controls
    case 'html':
    case 'article':
    case 'mermaid':
    case 'json-editor':
    case 'pdf-viewer':
    case 'iframe-viewer':
      return ['containerStyle', 'headerStyle', 'bodyStyle'];

    // Media controls
    case 'image':
    case 'video':
    case 'audio':
      return ['containerStyle', 'headerStyle', 'bodyStyle'];

    // File upload
    case 'file-upload':
      return ['containerStyle', 'headerStyle', 'bodyStyle', 'contentStyle', 'buttonStyle'];

    // Full-featured container controls
    case 'card-container':
    case 'modal':
    case 'form-container':
    case 'form-node':
      return ['containerStyle', 'headerStyle', 'bodyStyle', 'contentStyle', 'buttonStyle'];

    // Tab / accordion / panel controls
    case 'enhanced-tabs':
    case 'accordion':
    case 'collapsible-panel':
    case 'api-response-viewer':
      return ['containerStyle', 'headerStyle', 'bodyStyle', 'contentStyle'];

    // Stepper and data table
    case 'stepper':
    case 'data-table':
      return ['containerStyle', 'headerStyle', 'bodyStyle', 'contentStyle', 'buttonStyle'];

    // Grid
    case 'grid':
      return ['containerStyle', 'bodyStyle'];

    default:
      return ['containerStyle'];
  }
}

// ─── Section 7: buildGradientString ──────────────────────────────────────────

/**
 * Converts a {@link GradientConfig} to a valid CSS gradient function string.
 *
 * - Returns an empty string if fewer than two colour stops are provided.
 * - `linear` gradients use `linear-gradient(${angle}deg, …)`.
 * - `radial` gradients use `radial-gradient(circle, …)`.
 * - Stop positions are expressed as percentages when supplied.
 */
export function buildGradientString(config: GradientConfig): string {
  if (!config || !Array.isArray(config.stops) || config.stops.length < 2) {
    return '';
  }

  const stopList = config.stops
    .map((stop) =>
      stop.position !== undefined
        ? `${stop.color} ${stop.position}%`
        : stop.color,
    )
    .join(', ');

  if (config.type === 'linear') {
    const angle = config.angle ?? 180;
    return `linear-gradient(${angle}deg, ${stopList})`;
  }

  if (config.type === 'radial') {
    return `radial-gradient(circle, ${stopList})`;
  }

  return '';
}

// ─── Section 8: isEmptyStyleProperties ───────────────────────────────────────

/**
 * Returns `true` when every field of the given {@link StyleProperties} object
 * is `undefined` or `null`.
 *
 * Useful for hiding "no styles configured" states in the designer UI without
 * rendering empty panels.
 */
export function isEmptyStyleProperties(props: StyleProperties): boolean {
  return Object.values(props).every((v) => v === undefined || v === null);
}
